/**
 * FactorCorrelationsTab.jsx
 * 
 * DAS-1894: Factor Correlations tab for Backtesting page.
 * DAS-1915: Extended with regime and lookback window filters.
 * Displays correlation heatmap, data quality badges, and derived weights.
 * 
 * Components:
 * 1. Correlation Heatmap - fields × horizons matrix with color-coded cells
 * 2. Data Quality Badges - quality score per field with coverage details
 * 3. Correlation Details Panel - expandable details on cell click
 * 4. Derived Weights Display - comparison of data-driven vs hardcoded weights
 * 5. Regime Filter - filter by market regime (BULL/BEAR/NEUTRAL)
 * 6. Lookback Window Filter - filter by data window (5Y/1Y/3M)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  message,
  Spin,
  Row,
  Col,
  Statistic,
  Progress,
  Empty,
  Alert,
  Tooltip,
  Modal,
  Button,
  Descriptions,
  Divider,
  Badge,
  Select,
} from 'antd';
import {
  HeatMapOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  RiseOutlined,
  FallOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import api from '../../functions/api';

const { Text, Title } = Typography;

// ============================================================================
// Constants
// ============================================================================

const HORIZONS = [
  { days: 21, label: '1M' },
  { days: 63, label: '3M' },
  { days: 126, label: '6M' },
  { days: 252, label: '1Y' },
];

const HORIZON_LABELS = {
  21: '1M',
  63: '3M',
  126: '6M',
  252: '1Y',
};

// DAS-1915: Regime options
const REGIME_OPTIONS = [
  { value: 'ALL', label: '🌐 All Regimes', description: 'Universal correlations across all market conditions' },
  { value: 'BULL', label: '🐂 Bull Market', description: 'VIX < 20, index above 200-day MA' },
  { value: 'BEAR', label: '🐻 Bear Market', description: 'VIX > 25, index below 200-day MA' },
  { value: 'NEUTRAL', label: '⚖️ Neutral', description: 'Mixed conditions between bull and bear' },
];

// DAS-1915: Lookback window options
const WINDOW_OPTIONS = [
  { value: '5Y', label: '5 Years', description: 'Long-term stable correlations (~1250 trading days)', color: 'blue' },
  { value: '1Y', label: '1 Year', description: 'Recent regime patterns (~250 trading days)', color: 'green' },
  { value: '3M', label: '3 Months', description: 'Current momentum (~63 trading days)', color: 'orange' },
];

// Previous hardcoded weights for comparison (from DR_ticker_fundamentals.py)
const HARDCODED_WEIGHTS = {
  roa: 0.20,
  priceToBook: 0.15,
  profit_margins: 0.15,
  roe: 0.12,
  PE: 0.10,
  revenue_growth: 0.08,
  trailingPegRatio: 0.08,
  beta: 0.07,
  currentRatio: 0.05,
};

// Field descriptions for tooltips (DAS-1912)
const FIELD_DESCRIPTIONS = {
  roa: {
    name: 'Return on Assets (ROA)',
    description: 'Measures how efficiently a company uses its assets to generate profit.',
    calculation: 'Net Income ÷ Total Assets × 100',
    importance: 'Higher ROA indicates better asset utilization. Strong positive correlation with forward returns.',
  },
  roe: {
    name: 'Return on Equity (ROE)',
    description: 'Measures profitability relative to shareholder equity.',
    calculation: 'Net Income ÷ Shareholder Equity × 100',
    importance: 'Higher ROE suggests efficient use of equity capital.',
  },
  priceToBook: {
    name: 'Price to Book Ratio (P/B)',
    description: 'Compares market value to book value of equity.',
    calculation: 'Market Price per Share ÷ Book Value per Share',
    importance: 'Lower P/B may indicate undervaluation. Positive correlation suggests quality premium.',
  },
  PE: {
    name: 'Price to Earnings Ratio (P/E)',
    description: 'Compares stock price to earnings per share.',
    calculation: 'Market Price per Share ÷ Earnings per Share',
    importance: 'Traditional valuation metric. Lower P/E often considered "cheaper".',
  },
  profit_margins: {
    name: 'Profit Margin',
    description: 'Percentage of revenue retained as profit.',
    calculation: 'Net Income ÷ Revenue × 100',
    importance: 'Higher margins indicate pricing power and operational efficiency.',
  },
  revenue_growth: {
    name: 'Revenue Growth',
    description: 'Year-over-year percentage change in revenue.',
    calculation: '(Current Revenue - Prior Revenue) ÷ Prior Revenue × 100',
    importance: 'Indicates business expansion and market demand.',
  },
  beta: {
    name: 'Beta',
    description: 'Measures stock volatility relative to the market.',
    calculation: 'Covariance(Stock, Market) ÷ Variance(Market)',
    importance: 'Beta > 1 = more volatile than market. Used for risk assessment.',
  },
  Marketcap: {
    name: 'Market Capitalization',
    description: 'Total market value of all outstanding shares.',
    calculation: 'Share Price × Shares Outstanding',
    importance: 'Size factor - larger companies tend to be more stable.',
  },
  debtToEquity: {
    name: 'Debt to Equity Ratio',
    description: 'Measures financial leverage.',
    calculation: 'Total Debt ÷ Shareholder Equity × 100 (shown as a percentage: 150 = 1.5× equity)',
    importance: 'Higher ratio = more debt financing, higher risk.',
  },
  trailingPegRatio: {
    name: 'PEG Ratio',
    description: 'P/E ratio adjusted for earnings growth.',
    calculation: 'P/E Ratio ÷ Earnings Growth Rate',
    importance: 'PEG < 1 often considered undervalued relative to growth.',
  },
  priceToSalesTrailing: {
    name: 'Price to Sales (P/S)',
    description: 'Compares stock price to revenue per share.',
    calculation: 'Market Cap ÷ Total Revenue',
    importance: 'Useful for companies with negative earnings.',
  },
  heldPercentInsiders: {
    name: 'Insider Ownership %',
    description: 'Percentage of shares held by company insiders.',
    calculation: 'Insider Shares ÷ Total Shares × 100',
    importance: 'High insider ownership aligns management with shareholders.',
  },
  heldPercentInstitutions: {
    name: 'Institutional Ownership %',
    description: 'Percentage of shares held by institutional investors.',
    calculation: 'Institutional Shares ÷ Total Shares × 100',
    importance: 'High institutional ownership indicates professional confidence.',
  },
  yoy_growth: {
    name: 'Year-over-Year Growth',
    description: 'Annual percentage change in a key metric (typically earnings).',
    calculation: '(Current Year - Prior Year) ÷ Prior Year × 100',
    importance: 'Consistent YoY growth indicates business health.',
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color for correlation value (-1 to 1)
 * Green for positive, Red for negative, intensity based on magnitude
 */
const getCorrelationColor = (value) => {
  if (value === null || value === undefined) return '#f0f0f0';
  
  const absVal = Math.abs(value);
  // Scale: 0 = white, 0.05 = light color, 0.25+ = saturated color
  const intensity = Math.min(absVal * 4, 1);
  
  if (value > 0) {
    // Green gradient: white → light green → bright green
    // r: 255 → 50, g: 255 → 205, b: 255 → 50
    const red = Math.floor(255 - intensity * 205);
    const green = Math.floor(255 - intensity * 50);
    const blue = Math.floor(255 - intensity * 205);
    return `rgb(${red}, ${green}, ${blue})`;
  } else if (value < 0) {
    // Red gradient: white → light red → bright red
    const red = Math.floor(255 - intensity * 50);
    const green = Math.floor(255 - intensity * 205);
    const blue = Math.floor(255 - intensity * 205);
    return `rgb(${red}, ${green}, ${blue})`;
  }
  return '#f0f0f0';
};

/**
 * Get text color based on background (for readability)
 */
const getTextColor = (bgValue) => {
  if (bgValue === null || bgValue === undefined) return '#999';
  return Math.abs(bgValue) > 0.08 ? '#fff' : '#333';
};

/**
 * Quality score color
 */
const getQualityColor = (score) => {
  if (score >= 80) return '#52c41a';
  if (score >= 60) return '#faad14';
  return '#ff4d4f';
};

/**
 * Format correlation value for display
 */
const formatCorrelation = (value) => {
  if (value === null || value === undefined) return '-';
  const numVal = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numVal)) return '-';
  return numVal.toFixed(4);
};

/**
 * Format p-value for display
 */
const formatPValue = (value) => {
  if (value === null || value === undefined) return '-';
  const numVal = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numVal)) return '-';
  if (numVal < 0.0001) return '< 0.0001';
  return numVal.toFixed(6);
};

/**
 * Format sample size
 */
const formatSampleSize = (value) => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString();
};

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Quality Badge Component (DAS-1912: Enhanced tooltip)
 */
const QualityBadge = ({ score, field }) => {
  const color = getQualityColor(score);
  const icon = score >= 80 ? <CheckCircleOutlined /> : 
               score >= 60 ? <WarningOutlined /> : 
               <CloseCircleOutlined />;
  
  const tooltipContent = (
    <div style={{ maxWidth: 280 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Quality Score: {score}/100</div>
      <div style={{ fontSize: 11, marginBottom: 8 }}>
        <strong>How it's calculated:</strong>
        <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
          <li>Coverage % of non-null values in the dataset</li>
          <li>100% coverage = 100 score</li>
          <li>70%+ = acceptable for analysis</li>
        </ul>
      </div>
      <div style={{ fontSize: 11, color: '#ccc' }}>
        {score === 75 
          ? 'Note: 75 is the default score for sector-specific analysis where per-field validation is skipped for performance.'
          : score >= 80 
            ? '✓ Good data quality - reliable for analysis' 
            : score >= 60 
              ? '⚠ Moderate quality - interpret with caution'
              : '✗ Low quality - results may be unreliable'
        }
      </div>
    </div>
  );
  
  return (
    <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: 300 }}>
      <Tag color={color} icon={icon} style={{ fontSize: 11, cursor: 'help' }}>
        Q: {score}
      </Tag>
    </Tooltip>
  );
};

/**
 * Significance Indicator
 */
const SignificanceIndicator = ({ isSignificant, pValue }) => {
  const numPValue = typeof pValue === 'string' ? parseFloat(pValue) : pValue;
  
  if (!isSignificant) {
    return (
      <Tooltip title={`p-value: ${formatPValue(numPValue)} (not significant)`}>
        <Tag color="default" style={{ fontSize: 10 }}>NS</Tag>
      </Tooltip>
    );
  }
  
  // Show significance level
  let stars = '★';
  if (numPValue < 0.001) stars = '★★★';
  else if (numPValue < 0.01) stars = '★★';
  
  return (
    <Tooltip title={`p-value: ${formatPValue(numPValue)}`}>
      <Tag color="gold" style={{ fontSize: 10 }}>{stars}</Tag>
    </Tooltip>
  );
};

/**
 * Correlation Cell Component (for heatmap)
 */
const CorrelationCell = ({ data, onClick }) => {
  if (!data) {
    return (
      <div 
        style={{ 
          padding: '8px 4px', 
          textAlign: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 4,
          cursor: 'not-allowed'
        }}
      >
        <Text type="secondary">-</Text>
      </div>
    );
  }
  
  const bgColor = getCorrelationColor(data.correlation_coefficient);
  const textColor = getTextColor(data.correlation_coefficient);
  const coef = parseFloat(data.correlation_coefficient);
  
  return (
    <div
      onClick={() => onClick(data)}
      style={{
        padding: '8px 4px',
        textAlign: 'center',
        backgroundColor: bgColor,
        borderRadius: 4,
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: data.is_significant ? '2px solid #ffd700' : '1px solid #d9d9d9',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ color: textColor, fontWeight: 600, fontSize: 13 }}>
        {coef >= 0 ? '+' : ''}{formatCorrelation(coef)}
      </div>
      <div style={{ marginTop: 2 }}>
        <SignificanceIndicator isSignificant={data.is_significant} pValue={data.p_value} />
      </div>
    </div>
  );
};

/**
 * Derived Weight Bar (DAS-1912: Clean tooltip with actual calculation values)
 */
const DerivedWeightBar = ({ field, weightData, hardcodedWeight }) => {
  // weightData can be number (old format) or object with details (new format)
  const isDetailedFormat = typeof weightData === 'object' && weightData !== null;
  const dw = isDetailedFormat ? (weightData.weight || 0) : (typeof weightData === 'string' ? parseFloat(weightData) : (weightData || 0));
  const correlation = isDetailedFormat ? weightData.correlation : null;
  const totalSum = isDetailedFormat ? weightData.total_sum : null;
  const sampleSize = isDetailedFormat ? weightData.sample_size : null;
  
  const hw = typeof hardcodedWeight === 'string' ? parseFloat(hardcodedWeight) : (hardcodedWeight || 0);
  const maxWeight = Math.max(dw, hw, 0.25);
  const derivedPct = (dw / maxWeight) * 100;
  const hardcodedPct = (hw / maxWeight) * 100;
  
  const change = dw && hw 
    ? ((dw - hw) / hw * 100).toFixed(1)
    : null;
  
  const tooltipContent = (
    <div style={{ minWidth: 180, color: '#000', background: '#fff', padding: 4 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{field}</div>
      
      {/* Actual calculation with numbers */}
      {correlation !== null && totalSum !== null ? (
        <div style={{ 
          fontFamily: 'Monaco, Consolas, monospace', 
          fontSize: 11, 
          background: '#f8f8f8', 
          padding: 8, 
          borderRadius: 4,
          marginBottom: 8,
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ color: '#333' }}>r = <strong style={{ color: '#1890ff' }}>{correlation.toFixed(4)}</strong></div>
          <div style={{ color: '#333' }}>Σ = {totalSum.toFixed(4)}</div>
          <div style={{ borderTop: '1px solid #ddd', paddingTop: 4, marginTop: 4, color: '#333' }}>
            = {correlation.toFixed(4)} / {totalSum.toFixed(4)}
          </div>
          <div style={{ color: '#333', fontWeight: 600 }}>
            = <span style={{ color: '#52c41a' }}>{(dw * 100).toFixed(1)}%</span>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12, marginBottom: 8, color: '#333' }}>
          Weight: <strong>{(dw * 100).toFixed(1)}%</strong>
        </div>
      )}
      
      {/* Comparison */}
      <div style={{ fontSize: 11, color: '#333' }}>
        <div>Baseline: {hw ? (hw * 100).toFixed(1) + '%' : 'N/A'}</div>
        {change && (
          <div style={{ 
            color: parseFloat(change) > 0 ? '#389e0d' : '#cf1322',
            fontWeight: 600 
          }}>
            Δ {parseFloat(change) > 0 ? '+' : ''}{change}%
          </div>
        )}
        {sampleSize && <div style={{ color: '#666', marginTop: 4 }}>n = {sampleSize.toLocaleString()}</div>}
      </div>
    </div>
  );
  
  return (
    <Tooltip 
      title={tooltipContent} 
      placement="left"
      color="#fff"
      overlayInnerStyle={{ color: '#000' }}
    >
      <div style={{ marginBottom: 12, cursor: 'help' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <Text strong style={{ fontSize: 12 }}>{field}</Text>
          <Space size="small">
            {change && (
              <Tag 
                color={parseFloat(change) > 0 ? 'green' : parseFloat(change) < 0 ? 'red' : 'default'}
                style={{ fontSize: 10 }}
              >
                {parseFloat(change) > 0 ? '+' : ''}{change}%
              </Tag>
            )}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {(dw * 100).toFixed(1)}%
            </Text>
          </Space>
        </div>
        <div style={{ position: 'relative', height: 20 }}>
          {/* Hardcoded weight (background) */}
          <Progress
            percent={hardcodedPct}
            showInfo={false}
            strokeColor="#d9d9d9"
            trailColor="#f0f0f0"
            size="small"
            style={{ position: 'absolute', width: '100%' }}
          />
          {/* Derived weight (foreground) */}
          <Progress
            percent={derivedPct}
            showInfo={false}
            strokeColor={dw >= hw ? '#52c41a' : '#ff4d4f'}
            trailColor="transparent"
            size="small"
            style={{ position: 'absolute', width: '100%' }}
          />
        </div>
      </div>
    </Tooltip>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const FactorCorrelationsTab = ({ callAction }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [correlations, setCorrelations] = useState([]);
  const [derivedWeights, setDerivedWeights] = useState({});
  const [summary, setSummary] = useState(null);
  const [selectedHorizon, setSelectedHorizon] = useState(63);
  const [selectedCell, setSelectedCell] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // Sector filtering
  const [availableSectors, setAvailableSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState('ALL');
  
  // DAS-1915: Regime and lookback window filtering
  const [availableRegimes, setAvailableRegimes] = useState([]);
  const [selectedRegime, setSelectedRegime] = useState('ALL');
  const [availableWindows, setAvailableWindows] = useState([]);
  const [selectedWindow, setSelectedWindow] = useState('5Y');
  
  /**
   * Fetch available filter options on mount
   * DAS-1915: Extended to fetch regimes and windows
   */
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [sectorsRes, regimesRes, windowsRes] = await Promise.all([
          api.post('/backtesting', { action: 'correlation_get_sectors', payload: {} }),
          api.post('/backtesting', { action: 'correlation_get_regimes', payload: {} }),
          api.post('/backtesting', { action: 'correlation_get_windows', payload: {} }),
        ]);
        
        if (sectorsRes.data.success && sectorsRes.data.data) {
          setAvailableSectors(sectorsRes.data.data);
        }
        if (regimesRes.data.success && regimesRes.data.data) {
          setAvailableRegimes(regimesRes.data.data);
        }
        if (windowsRes.data.success && windowsRes.data.data) {
          setAvailableWindows(windowsRes.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);
  
  /**
   * Fetch correlation data from API
   * DAS-1915: Extended with regime and lookback_window parameters
   */
  const fetchCorrelations = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch latest correlations for selected filters
      const [correlationRes, weightsRes, summaryRes] = await Promise.all([
        api.post('/backtesting', {
          action: 'correlation_get_latest',
          payload: { 
            sector: selectedSector,
            regime: selectedRegime,
            lookback_window: selectedWindow,
          }
        }),
        api.post('/backtesting', {
          action: 'correlation_get_weights',
          payload: { 
            horizon_days: selectedHorizon, 
            sector: selectedSector,
            regime: selectedRegime,
            lookback_window: selectedWindow,
          }
        }),
        api.post('/backtesting', {
          action: 'correlation_get_summary',
          payload: {}
        })
      ]);
      
      if (correlationRes.data.success) {
        setCorrelations(correlationRes.data.data || []);
      } else {
        console.error('Failed to fetch correlations:', correlationRes.data.error);
      }
      
      if (weightsRes.data.success) {
        setDerivedWeights(weightsRes.data.data || {});
      }
      
      if (summaryRes.data.success) {
        setSummary(summaryRes.data.data || null);
      }
      
    } catch (err) {
      console.error('Failed to fetch correlation data:', err);
      message.error('Failed to load correlation data');
    } finally {
      setLoading(false);
    }
  }, [selectedHorizon, selectedSector, selectedRegime, selectedWindow]);
  
  // Fetch on mount and when horizon/sector changes
  useEffect(() => {
    fetchCorrelations();
  }, [fetchCorrelations]);
  
  /**
   * Build heatmap data structure
   * Rows: fields, Columns: horizons
   */
  const buildHeatmapData = () => {
    // Group correlations by field
    const byField = {};
    
    correlations.forEach(c => {
      const field = c.field_name;
      if (!byField[field]) {
        byField[field] = {
          field,
          quality_score: c.quality_score || 0,
        };
      }
      // Add horizon data
      const horizonKey = `h${c.horizon_days}`;
      byField[field][horizonKey] = c;
    });
    
    // Convert to array and sort by quality score (descending)
    return Object.values(byField).sort((a, b) => {
      // Sort by correlation strength at selected horizon
      const aCorr = a[`h${selectedHorizon}`]?.correlation_coefficient || 0;
      const bCorr = b[`h${selectedHorizon}`]?.correlation_coefficient || 0;
      return Math.abs(bCorr) - Math.abs(aCorr);
    });
  };
  
  const heatmapData = buildHeatmapData();
  
  /**
   * Handle cell click - show details
   */
  const handleCellClick = (data) => {
    setSelectedCell(data);
    setDetailModalOpen(true);
  };
  
  /**
   * Table columns for heatmap (DAS-1912: Field name tooltips)
   */
  const heatmapColumns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: 180,
      fixed: 'left',
      render: (field, record) => {
        const fieldInfo = FIELD_DESCRIPTIONS[field];
        const fieldTooltip = fieldInfo ? (
          <div style={{ maxWidth: 280 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#52c41a' }}>
              {fieldInfo.name}
            </div>
            <div style={{ marginBottom: 8 }}>{fieldInfo.description}</div>
            <div style={{ fontSize: 11, marginBottom: 6 }}>
              <strong>Calculation:</strong> {fieldInfo.calculation}
            </div>
            <div style={{ fontSize: 11, color: '#ccc', fontStyle: 'italic' }}>
              {fieldInfo.importance}
            </div>
          </div>
        ) : field;
        
        return (
          <Space direction="vertical" size={0}>
            <Tooltip title={fieldTooltip} overlayStyle={{ maxWidth: 300 }} placement="right">
              <Text strong style={{ fontSize: 13, cursor: 'help' }}>
                {field} <InfoCircleOutlined style={{ fontSize: 10, color: '#999' }} />
              </Text>
            </Tooltip>
            <QualityBadge score={record.quality_score} field={field} />
          </Space>
        );
      },
    },
    ...HORIZONS.map(h => ({
      title: (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 600 }}>{h.label}</div>
          <div style={{ fontSize: 10, color: '#999' }}>{h.days}d</div>
        </div>
      ),
      key: `h${h.days}`,
      width: 100,
      align: 'center',
      render: (_, record) => (
        <CorrelationCell 
          data={record[`h${h.days}`]} 
          onClick={handleCellClick}
        />
      ),
    })),
  ];
  
  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <Alert
        message="Factor Correlation Analysis"
        description={
          <span>
            View correlations between fundamental factors and forward stock returns. 
            Click on cells for detailed statistics. Derived weights are calculated from correlation strength.
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              DAS-1915: Filter by market regime and data window to see how correlations change in different conditions.
            </Text>
          </span>
        }
        type="info"
        showIcon
        icon={<HeatMapOutlined />}
        style={{ marginBottom: 16 }}
      />
      
      {/* DAS-1915: Filter Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong style={{ marginRight: 8 }}>Filters:</Text>
          </Col>
          
          {/* Sector Filter */}
          <Col>
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 11 }}>Sector</Text>
              <Select
                value={selectedSector}
                onChange={setSelectedSector}
                style={{ width: 160 }}
                size="small"
              >
                {availableSectors.map(s => (
                  <Select.Option key={s.sector} value={s.sector}>
                    {s.sector === 'ALL' ? '🌍 All Sectors' : s.sector}
                    {s.sector !== 'ALL' && <Tag style={{ marginLeft: 4 }} color="blue">{s.field_count}f</Tag>}
                  </Select.Option>
                ))}
                {availableSectors.length === 0 && (
                  <Select.Option value="ALL">🌍 All Sectors</Select.Option>
                )}
              </Select>
            </Space>
          </Col>
          
          {/* Regime Filter (DAS-1915) */}
          <Col>
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 11 }}>Regime</Text>
              <Tooltip 
                title={
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Market Regime</div>
                    {REGIME_OPTIONS.map(r => (
                      <div key={r.value} style={{ fontSize: 11, marginBottom: 4 }}>
                        <strong>{r.label}:</strong> {r.description}
                      </div>
                    ))}
                  </div>
                }
              >
                <Select
                  value={selectedRegime}
                  onChange={setSelectedRegime}
                  style={{ width: 150 }}
                  size="small"
                >
                  {REGIME_OPTIONS.map(r => {
                    const availableData = availableRegimes.find(ar => ar.regime === r.value);
                    // Only disable if we HAVE data and this option is NOT in it (always allow ALL)
                    const shouldDisable = availableRegimes.length > 0 && availableData === undefined && r.value !== 'ALL';
                    return (
                      <Select.Option 
                        key={r.value} 
                        value={r.value}
                        disabled={shouldDisable}
                      >
                        {r.label}
                        {availableData && availableData.result_count > 0 && (
                          <Tag style={{ marginLeft: 4 }} color="cyan">{availableData.result_count}</Tag>
                        )}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Tooltip>
            </Space>
          </Col>
          
          {/* Lookback Window Filter (DAS-1915) */}
          <Col>
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 11 }}>Window</Text>
              <Tooltip 
                title={
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Data Lookback Window</div>
                    {WINDOW_OPTIONS.map(w => (
                      <div key={w.value} style={{ fontSize: 11, marginBottom: 4 }}>
                        <strong>{w.label}:</strong> {w.description}
                      </div>
                    ))}
                  </div>
                }
              >
                <Select
                  value={selectedWindow}
                  onChange={setSelectedWindow}
                  style={{ width: 120 }}
                  size="small"
                >
                  {WINDOW_OPTIONS.map(w => {
                    const availableData = availableWindows.find(aw => aw.lookback_window === w.value);
                    // Only disable if we HAVE data and this option is NOT in it
                    const shouldDisable = availableWindows.length > 0 && availableData === undefined;
                    return (
                      <Select.Option 
                        key={w.value} 
                        value={w.value}
                        disabled={shouldDisable}
                      >
                        <Tag color={w.color} style={{ marginRight: 4 }}>{w.label}</Tag>
                        {availableData && availableData.result_count > 0 && (
                          <span style={{ fontSize: 10, color: '#999' }}>({availableData.result_count})</span>
                        )}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Tooltip>
            </Space>
          </Col>
          
          {/* Refresh Button */}
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Space>
              {/* Current filter summary */}
              <Text type="secondary" style={{ fontSize: 11 }}>
                {selectedRegime !== 'ALL' && <Tag color="purple">{selectedRegime}</Tag>}
                {selectedWindow !== '5Y' && <Tag color="orange">{selectedWindow}</Tag>}
                {selectedSector !== 'ALL' && <Tag color="blue">{selectedSector}</Tag>}
              </Text>
              <Button 
                size="small" 
                icon={<ReloadOutlined />} 
                onClick={fetchCorrelations}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      
      {/* Summary Stats */}
      {summary && summary.has_data && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={4}>
              <Statistic
                title="Total Correlations"
                value={summary.total_results || 0}
                prefix={<BarChartOutlined />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Significant"
                value={summary.significant_count || 0}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Fields Analyzed"
                value={summary.fields_analyzed || summary.unique_fields || 0}
                prefix={<ExperimentOutlined />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Horizons"
                value={summary.horizons_analyzed || summary.unique_horizons || 0}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Data Range"
                value={(() => {
                  // Check various possible field names from the API
                  const start = summary.date_range?.start || summary.data_start || summary.data_range?.start;
                  const end = summary.date_range?.end || summary.data_end || summary.data_range?.end;
                  if (start && end) return `${start} → ${end}`;
                  if (summary.latest_calculation_date) return `Latest: ${summary.latest_calculation_date}`;
                  return '-';
                })()}
                valueStyle={{ fontSize: 14 }}
              />
            </Col>
          </Row>
        </Card>
      )}
      
      {loading && !correlations.length ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading correlation data...</div>
        </div>
      ) : correlations.length === 0 ? (
        <Empty
          description="No correlation data available. Run the correlation analysis first."
          style={{ marginTop: 48 }}
        />
      ) : (
        <Row gutter={16}>
          {/* Heatmap */}
          <Col span={16}>
            <Card 
              title={
                <Space>
                  <HeatMapOutlined />
                  <span>Correlation Heatmap</span>
                  <Tag color="blue">{heatmapData.length} fields</Tag>
                </Space>
              }
              size="small"
            >
              <Table
                dataSource={heatmapData}
                columns={heatmapColumns}
                rowKey="field"
                pagination={false}
                size="small"
                scroll={{ x: 600 }}
                style={{ marginTop: 8 }}
              />
              
              {/* Legend */}
              <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>Legend:</strong>{' '}
                  <span style={{ 
                    background: 'linear-gradient(90deg, #ff6b6b, #f0f0f0, #52c41a)', 
                    padding: '2px 16px', 
                    borderRadius: 2,
                    marginLeft: 8
                  }}>
                    Negative ← 0 → Positive
                  </span>
                  {' | '}
                  <Tag color="gold" style={{ marginLeft: 8 }}>★</Tag> Statistically significant
                  {' | '}
                  <Tag color="default">NS</Tag> Not significant
                </Text>
              </div>
            </Card>
          </Col>
          
          {/* Derived Weights */}
          <Col span={8}>
            <Card 
              title={
                <Space>
                  <BarChartOutlined />
                  <span>Derived Weights</span>
                  <Tooltip title="Weights derived from correlation coefficients vs hardcoded baseline. DAS-1915: Now filtered by regime and lookback window.">
                    <InfoCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </Space>
              }
              extra={
                <Space size={4}>
                  <Tag color="purple">{HORIZON_LABELS[selectedHorizon]}</Tag>
                  {selectedRegime !== 'ALL' && (
                    <Tag color={selectedRegime === 'BULL' ? 'green' : selectedRegime === 'BEAR' ? 'red' : 'orange'}>
                      {selectedRegime}
                    </Tag>
                  )}
                  {selectedWindow !== '5Y' && (
                    <Tag color={selectedWindow === '1Y' ? 'cyan' : 'orange'}>
                      {selectedWindow}
                    </Tag>
                  )}
                </Space>
              }
              size="small"
            >
              {Object.keys(derivedWeights).length === 0 ? (
                <Empty 
                  description="No derived weights available" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <>
                  <div style={{ marginBottom: 16, fontSize: 11 }}>
                    <Space>
                      <Badge color="#52c41a" text="Derived weight" />
                      <Badge color="#d9d9d9" text="Hardcoded baseline" />
                    </Space>
                  </div>
                  
                  {Object.entries(derivedWeights)
                    .sort((a, b) => {
                      // Sort by weight value (handle both old and new format)
                      const aWeight = typeof a[1] === 'object' ? a[1].weight : a[1];
                      const bWeight = typeof b[1] === 'object' ? b[1].weight : b[1];
                      return bWeight - aWeight;
                    })
                    .map(([field, weightData]) => (
                      <DerivedWeightBar
                        key={field}
                        field={field}
                        weightData={weightData}
                        hardcodedWeight={HARDCODED_WEIGHTS[field]}
                      />
                    ))
                  }
                  
                  <Divider style={{ margin: '16px 0' }} />
                  
                  <div style={{ fontSize: 11, color: '#666' }}>
                    <Text type="secondary">
                      Weights are normalized to sum to 1.0 and derived from 
                      correlation coefficients at the {HORIZON_LABELS[selectedHorizon]} forward horizon.
                      Only statistically significant positive correlations are included.
                    </Text>
                  </div>
                </>
              )}
            </Card>
          </Col>
        </Row>
      )}
      
      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <BarChartOutlined />
            <span>Correlation Details</span>
            {selectedCell && (
              <Tag color="blue">{selectedCell.field_name}</Tag>
            )}
          </Space>
        }
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={null}
        width={600}
      >
        {selectedCell && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Field" span={2}>
              <Space direction="vertical" size={0}>
                <Text strong>{selectedCell.field_name}</Text>
                {FIELD_DESCRIPTIONS[selectedCell.field_name] && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {FIELD_DESCRIPTIONS[selectedCell.field_name].description}
                  </Text>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Horizon">
              {selectedCell.horizon_label || `${selectedCell.horizon_days} days`}
            </Descriptions.Item>
            <Descriptions.Item label="Sector">
              {selectedCell.sector || 'ALL'}
            </Descriptions.Item>
            <Descriptions.Item label="Regime">
              <Tag color={
                selectedCell.regime === 'BULL' ? 'green' :
                selectedCell.regime === 'BEAR' ? 'red' :
                selectedCell.regime === 'NEUTRAL' ? 'orange' : 'default'
              }>
                {selectedCell.regime || 'ALL'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Lookback Window">
              <Tag color={
                selectedCell.lookback_window === '5Y' ? 'blue' :
                selectedCell.lookback_window === '1Y' ? 'green' :
                selectedCell.lookback_window === '3M' ? 'orange' : 'default'
              }>
                {selectedCell.lookback_window || '5Y'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Correlation Coefficient" span={2}>
              <Space>
                <Text 
                  strong 
                  style={{ 
                    fontSize: 18,
                    color: selectedCell.correlation_coefficient >= 0 ? '#52c41a' : '#ff4d4f'
                  }}
                >
                  {parseFloat(selectedCell.correlation_coefficient) >= 0 ? '+' : ''}
                  {formatCorrelation(parseFloat(selectedCell.correlation_coefficient))}
                </Text>
                {selectedCell.correlation_coefficient >= 0 
                  ? <RiseOutlined style={{ color: '#52c41a' }} />
                  : <FallOutlined style={{ color: '#ff4d4f' }} />
                }
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="P-Value">
              {formatPValue(parseFloat(selectedCell.p_value))}
            </Descriptions.Item>
            <Descriptions.Item label="Significant">
              {selectedCell.is_significant 
                ? <Tag color="success">Yes ★</Tag>
                : <Tag color="default">No</Tag>
              }
            </Descriptions.Item>
            <Descriptions.Item label="T-Statistic">
              {selectedCell.t_statistic ? parseFloat(selectedCell.t_statistic).toFixed(4) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sample Size">
              {formatSampleSize(selectedCell.sample_size)}
            </Descriptions.Item>
            <Descriptions.Item label="Quality Score">
              <Tooltip title={
                selectedCell.quality_score === 75
                  ? "Default score for sector analysis (per-field validation skipped for performance)"
                  : "Based on data coverage % (non-null values in dataset)"
              }>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Progress 
                    percent={selectedCell.quality_score || 0} 
                    size="small" 
                    strokeColor={getQualityColor(selectedCell.quality_score || 0)}
                    style={{ width: 120 }}
                  />
                  <InfoCircleOutlined style={{ color: '#999', cursor: 'help' }} />
                </div>
              </Tooltip>
            </Descriptions.Item>
            <Descriptions.Item label="Significance Threshold">
              {selectedCell.significance_threshold 
                ? parseFloat(selectedCell.significance_threshold).toFixed(5)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Data Period" span={2}>
              {selectedCell.data_start_date} → {selectedCell.data_end_date}
            </Descriptions.Item>
            <Descriptions.Item label="Calculated" span={2}>
              {selectedCell.calculation_date}
            </Descriptions.Item>
          </Descriptions>
        )}
        
        {selectedCell && (
          <Alert
            style={{ marginTop: 16 }}
            message="Interpretation"
            description={
              <>
                <p style={{ margin: 0 }}>
                  {selectedCell.correlation_coefficient >= 0 
                    ? `Higher ${selectedCell.field_name} values are associated with higher ${selectedCell.horizon_label || selectedCell.horizon_days + '-day'} forward returns.`
                    : `Higher ${selectedCell.field_name} values are associated with lower ${selectedCell.horizon_label || selectedCell.horizon_days + '-day'} forward returns.`
                  }
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#666' }}>
                  {selectedCell.is_significant 
                    ? 'This relationship is statistically significant.'
                    : 'This relationship is not statistically significant and should be interpreted with caution.'
                  }
                </p>
              </>
            }
            type={selectedCell.is_significant ? 'success' : 'warning'}
            showIcon
          />
        )}
      </Modal>
    </div>
  );
};

export default FactorCorrelationsTab;
